import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Option } from '../../../form/elements/select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter, faSearch, faTimes } from '@fortawesome/pro-solid-svg-icons';
import css from './index.module.css';
import { useHistory } from 'react-router-dom';
import { useQuery } from '../../../../hooks/use-query';

interface SelectableOption extends Option {
  isVisible?: boolean;
}

interface Props {
  options: SelectableOption[];
  name: string;
}

export function DefaultFilter({ name, options = [] }: Props) {
  const { t } = useTranslation();
  const history = useHistory();
  const elemRef = useRef(null);
  const [params] = useQuery();

  const [isOpened, setOpened] = useState(false);

  const [items, setItems] = useState<SelectableOption[]>([]);
  // Initialize selected items from search query only once on first render.
  const [selected, setSelected] = useState(() => {
    if (!params[name]) {
      return [];
    }

    return Array.isArray(params[name]) ? params[name] : [params[name]];
  });

  // On options change, update the possible items.
  useEffect(() => {
    if (!options) {
      return;
    }

    setItems(options.map((option) => ({ ...option, isVisible: true })));
  }, [options, setItems]);

  // Handles isActive status on its own, based on whats happening to selected.
  const [isActive, setActive] = useState(false);
  useEffect(() => {
    setActive(!!selected.length);
  }, [setActive, selected]);

  // Push items changes to query params if there are differences.
  useEffect(() => {
    const search = new URLSearchParams(history.location.search);
    search.delete(name);

    items
      .filter((item) => selected.indexOf(item.value) !== -1)
      .forEach((item: SelectableOption) => search.append(name, item.value));

    if (
      new URLSearchParams(history.location.search).toString() !==
      search.toString()
    ) {
      search.delete('page');
      history.push({ ...history.location, search: search.toString() });
    }
  }, [history, items, selected, name]);

  const toggleFilter = useCallback(
    () => setOpened((prevState) => !prevState),
    [setOpened]
  );
  const closeFilter = useCallback(
    (event: any): void => {
      event.stopPropagation();

      setOpened(false);
    },
    [setOpened]
  );
  const clearFilter = useCallback(
    (event: any): void => {
      event.stopPropagation();

      setOpened(false);
      setSelected([]);
    },
    [setOpened, setSelected]
  );
  const toggleSelected = useCallback(
    (option: SelectableOption) => {
      const index = selected.indexOf(option.value);

      if (index !== -1) {
        // Is selected, filter it out.
        setSelected(selected.filter((s: string) => s !== option.value));
      } else {
        // Not selected, add it.
        setSelected([...selected, option.value]);
      }
    },
    [selected, setSelected]
  );
  const onSearch = (value: string = '') => {
    setItems(
      items.map((option: SelectableOption) => ({
        ...option,
        isVisible: option.label.toLowerCase().includes(value.toLowerCase()),
      }))
    );
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpened && !elemRef.current?.contains(event.target)) {
        closeFilter(event);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpened, elemRef, closeFilter]);

  const results = items.map(
    (option: SelectableOption, index: number) =>
      option.isVisible && (
        <div
          className={css['item']}
          key={`checkbox-${index}`}
          onClick={() => toggleSelected(option)}
        >
          <input
            type="checkbox"
            id={`checkbox-${index}`}
            name={`checkbox-${index}`}
            onChange={() => null}
            checked={selected.indexOf(option.value) !== -1}
            className={css['checkbox']}
          />
          <div className="ml-2">{option.label}</div>
        </div>
      )
  );

  const classNames = `${css['filter-wrapper']} ${
    isOpened ? css['opened'] : ''
  } ${isActive ? css['active'] : ''}`;

  return options ? (
    <div className={classNames} ref={elemRef}>
      <div className={css['filter']} onClick={toggleFilter}>
        <FontAwesomeIcon icon={faFilter} />
        <span>{t('components.table.filter')}</span>
        <span className={css['close']} onClick={clearFilter}>
          <FontAwesomeIcon icon={faTimes} />
        </span>
      </div>

      <div className={css['menu']}>
        <div className={css['search-wrapper']}>
          <div className={css['search-container']}>
            <input
              ref={(input) => input && input.focus()}
              type={'text'}
              name="filter-search"
              placeholder={t('components.table.search')}
              className={css['search']}
              onChange={(event) => {
                onSearch(event.target.value);
              }}
              autoComplete="off"
            />
            <FontAwesomeIcon icon={faSearch} />
          </div>
        </div>

        <div className={css['results']}>{results}</div>
      </div>
    </div>
  ) : null;
}
